import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { BarChart3, CalendarDays, MapPin, Plus, ArrowLeft, Filter } from "lucide-react";
import { getEvents, getEventSummary, formatCurrency } from "../lib/storage";
import { useProState } from "../lib/pro";
import ProBadge from "../components/ProBadge";
import UpgradeButton from "../components/UpgradeButton";
import Paywall from "../components/Paywall";

const filters = [
  { key: "all", label: "All events" },
  { key: "worth", label: "Worth repeating" },
  { key: "needs-review", label: "Needs review" },
  { key: "not-worth", label: "Not worth repeating" },
  { key: "incomplete", label: "Report pending" },
];

const verdictTone = {
  good: "bg-brand-success/10 text-brand-success border-brand-success/30",
  risky: "bg-brand-warning/15 text-brand-warning border-brand-warning/30",
  bad: "bg-brand-error/10 text-brand-error border-brand-error/30",
  neutral: "bg-brand-cream-soft text-brand-mute border-brand-line",
};

export default function Compare() {
  const { pro, loading } = useProState();
  const [events, setEvents] = useState([]);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    setEvents(getEvents().sort((a, b) => (b.date || "").localeCompare(a.date || "")));
  }, []);

  const enriched = useMemo(
    () => events.map((e) => ({ event: e, summary: getEventSummary(e) })),
    [events],
  );

  const visible = useMemo(
    () => enriched.filter(({ summary }) => filter === "all" || summary.repeat.key === filter),
    [enriched, filter],
  );

  if (loading) return null;

  if (!pro) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 pb-24 md:pb-16" data-testid="compare-page">
        <Link to="/planner" className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-mute hover:text-brand-green mb-6" data-testid="compare-back">
          <ArrowLeft className="w-4 h-4" /> Back to planner
        </Link>
        <Paywall
          title="Compare every event side-by-side"
          description="Pro unlocks the comparison view — see which events are worth repeating and which ones to skip."
          features={["Side-by-side estimate vs. actual", "Filter by repeat verdict", "Unlimited saved events", "Full event history"]}
          testid="compare-paywall"
        />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 pb-24 md:pb-16" data-testid="compare-page">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
        <Link to="/planner" className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-mute hover:text-brand-green" data-testid="compare-back">
          <ArrowLeft className="w-4 h-4" /> Back to planner
        </Link>
        <UpgradeButton variant="ghost" label="Upgrade for full history" testid="compare-upgrade-btn" />
      </div>

      <div className="mt-4 flex items-start justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="flex items-center gap-2">
            <p className="label-eyebrow">Event comparison</p>
            <ProBadge />
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-brand-ink mt-2">
            Which events are worth repeating?
          </h1>
          <p className="text-brand-mute text-sm mt-1.5 max-w-lg">
            Side-by-side metrics across every saved event — estimate vs. actual, booth fee, sales, and repeat recommendation.
          </p>
        </div>
      </div>

      {/* Filter pills */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2 mb-6 -mx-1 px-1" role="tablist" data-testid="compare-filters">
        <span className="text-xs text-brand-mute shrink-0 inline-flex items-center gap-1 pr-1">
          <Filter className="w-3.5 h-3.5" /> Filter:
        </span>
        {filters.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`shrink-0 px-3.5 py-1.5 rounded-full text-sm font-medium transition-colors border ${
              filter === f.key
                ? "bg-brand-green text-white border-brand-green"
                : "bg-white text-brand-ink border-brand-line hover:border-brand-gold/40"
            }`}
            data-testid={`compare-filter-${f.key}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {events.length === 0 ? (
        <EmptyState />
      ) : visible.length === 0 ? (
        <div className="pop-card p-10 text-center" data-testid="compare-no-results">
          <p className="font-heading text-lg font-semibold text-brand-ink">No events match this filter.</p>
          <p className="text-sm text-brand-mute mt-1.5">Try another filter or complete more reports.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="compare-grid">
          {visible.map(({ event, summary }) => (
            <CompareCard key={event.id} event={event} summary={summary} />
          ))}
        </div>
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="pop-card p-10 text-center" data-testid="compare-empty">
      <span className="inline-grid place-items-center w-14 h-14 rounded-2xl bg-brand-green/10 text-brand-green">
        <BarChart3 className="w-7 h-7" strokeWidth={2} />
      </span>
      <h3 className="font-heading text-xl font-semibold text-brand-ink mt-4">No events to compare yet</h3>
      <p className="text-brand-mute text-sm mt-1.5">Add a few events and complete their post-event reports to see them here.</p>
      <Link to="/planner" className="pop-btn-primary inline-flex mt-5" data-testid="compare-add-event">
        <Plus className="w-4 h-4" /> Add event
      </Link>
    </div>
  );
}

function CompareCard({ event, summary }) {
  const dateLabel = event.date
    ? new Date(event.date + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : "Date TBD";
  const hasActual = !!summary.actual;
  const sales = summary.actual?.sales ?? null;

  return (
    <Link
      to={`/event/${event.id}`}
      className="pop-card pop-card-hover p-5 flex flex-col"
      data-testid={`compare-card-${event.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-heading text-lg font-semibold text-brand-ink truncate">{event.name}</h3>
          <p className="text-xs text-brand-mute mt-1 flex items-center gap-1.5">
            <CalendarDays className="w-3.5 h-3.5" strokeWidth={2} /> {dateLabel}
          </p>
          {event.location && (
            <p className="text-xs text-brand-mute mt-1 flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5" strokeWidth={2} /> {event.location}
            </p>
          )}
        </div>
        <span className={`pop-pill border ${verdictTone[summary.repeat.tone]} shrink-0`}>
          {summary.repeat.label}
        </span>
      </div>

      <div className="mt-5 grid grid-cols-2 gap-3">
        <Metric label="Est. profit" value={formatCurrency(summary.estimate.profit)} />
        <Metric label="Actual profit" value={hasActual ? formatCurrency(summary.actual.profit) : "—"} muted={!hasActual} good={hasActual} />
        <Metric label="Booth fee" value={formatCurrency(+event.boothFee || 0)} />
        <Metric label="Sales" value={sales !== null ? `${sales}` : (event.expectedSales ? `~${event.expectedSales} est.` : "—")} muted={!hasActual && !event.expectedSales} />
      </div>

      {!hasActual && (
        <p className="mt-4 text-xs text-brand-mute italic">Post-event report not completed.</p>
      )}
    </Link>
  );
}

function Metric({ label, value, muted = false, good = false }) {
  return (
    <div className="rounded-xl bg-brand-cream-soft p-3">
      <p className="text-[10px] uppercase tracking-wider font-semibold text-brand-mute">{label}</p>
      <p className={`font-heading text-base sm:text-lg font-semibold mt-1 tabular-nums ${
        muted ? "text-brand-mute/70" : good ? "text-brand-green" : "text-brand-ink"
      }`}>
        {value}
      </p>
    </div>
  );
}
